import { ApolloProvider } from '@apollo/client';
import { CKIconGradient } from '@clearkit/icons';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import queryString from 'query-string';
import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import { Provider } from 'unstated';
import UNSTATED from 'unstated-debug';

UNSTATED.enabled = process.env.NODE_ENV === 'development';
UNSTATED.logStateChanges = false;

import MaintenanceMode from '~/components/MaintenanceMode';
import MissingPage from '~/components/MissingPage';
import NPS from '~/components/NPS';
import SegmentsMenu from '~/components/SegmentsMenu';
import SidebarDrawer from '~/components/SidebarDrawer';
import ToastStack from '~/components/ToastStack';
import apollo from '~/lib/apollo';
import { integrationTypes } from '~/lib/dataEnrichment';
import {
  userHasAudiencesAccess,
  userHasCompaniesAccess,
  userHasDashboardAccess,
  userHasDataEnrichmentAccess,
  userHasDataEnrichmentLogsAccess,
  userHasFormsAccess,
  userHasICP,
  userHasInternalAccess,
  userHasPeopleAccess,
  userHasSettingsAccess,
  userHasSetupAccess,
} from '~/lib/gates';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { segmentTypes } from '~/lib/segments';

const Audience = lazy(() => import('~/pages/audiences/Show'));
const AudienceEdit = lazy(() => import('~/pages/audiences/Edit'));
const AudienceClone = lazy(() => import('~/pages/audiences/Clone'));
const AudienceNew = lazy(() => import('~/pages/audiences/New'));
const Audiences = lazy(() => import('~/pages/audiences/List'));
const AudiencesAccess = lazy(() => import('~/pages/audiences/Access'));
import ConfigureROIMetrics from '~/pages/audiences/ConfigureROIMetrics';
import ExportSalesPipeline from '~/pages/audiences/ExportSalesPipeline';
const Companies = lazy(() => import('~/pages/companies/List'));
const CompaniesSegment = lazy(() => import('~/pages/companies/Segment'));
import Company from '~/pages/companies/Show';
const Disconnected = lazy(() =>
  import('~/pages/configurations/Show/Disconnected'),
);
const TagsJs = lazy(() => import('~/pages/configurations/Show/TagsJs'));
const Segment = lazy(() => import('~/pages/configurations/Show/Segment'));
const Configurations = lazy(() => import('~/pages/configurations/List'));
const ContactSuccess = lazy(() => import('~/pages/ContactSuccess'));
const IdealCustomerProfile = lazy(() => import('~/pages/IdealCustomerProfile'));
const IdealCustomerProfileNew = lazy(() =>
  import('~/pages/IdealCustomerProfile/New'),
);

import ImportCsv from '~/pages/CsvImport';
import MapCsv from '~/pages/CsvImport/Map';
const Dashboard = lazy(() => import('~/pages/Dashboard'));
import NewsDetail from '~/pages/NewsDetail';
import NoAccess from '~/pages/NoAccess';
const People = lazy(() => import('~/pages/people/List'));
const PeopleSegment = lazy(() => import('~/pages/people/Segment'));
import Person from '~/pages/people/Show';
const Sandbox = lazy(() => import('~/pages/Sandbox'));
import SegmentDestinationSkeleton from '~/pages/destinations/destinationConfigs/SkeletonForm';
import SegmentDestinationEdit from '~/pages/destinations/Edit';
import SegmentDestinations from '~/pages/destinations/List';
import SegmentDestinationNew from '~/pages/destinations/New';
import EnrichmentAccess from '~/pages/enrichment/Access';
import EnrichmentList from '~/pages/enrichment/List';
import EnrichmentLogs from '~/pages/enrichment/Logs';
import EnrichmentLog from '~/pages/enrichment/LogsShow';
import EnrichmentShow from '~/pages/enrichment/Show';
import FormsAccess from '~/pages/forms/Access';
import FormsList from '~/pages/forms/List';
import FormsShow from '~/pages/forms/Show';
const SegmentEdit = lazy(() => import('~/pages/SegmentEdit'));
const SegmentNew = lazy(() => import('~/pages/SegmentNew'));
const Welcome = lazy(() => import('~/pages/Welcome'));
const Onboarding = lazy(() => import('~/pages/onboarding'));

const ConfigurationPanelFactory = lazy(() =>
  import('~/pages/configurations/Show/ConfigurationPanelFactory'),
);

import styles from './App.style';
import Gatekeeper from './app/Gatekeeper';
import Sidebar from './app/Sidebar';
import theme from './app/theme';
import ErrorHandler from './errorHandler';
import GatedRoute from './GatedRoute';
import ImpersonationWarning from './ImpersonationWarning';
import LoadingArea from './LoadingArea';

const UUIDRegex =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const queryClient = new QueryClient();
const concatEnrichmentTypes = integrationTypes.join('|');

class App extends React.Component {
  render() {
    const { classes } = this.props;

    return (
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={apollo}>
          <Provider>
            <Helmet defaultTemplate="Clearbit" titleTemplate="%s – Clearbit" />

            <CKIconGradient />
            <ToastStack />

            <Router history={history}>
              <MuiThemeProvider theme={theme}>
                <Gatekeeper />
                <MaintenanceMode />
                <NPS />

                <div className={classes.root}>
                  <ImpersonationWarning />
                  <Sidebar />

                  <ErrorHandler>
                    <article className={classes.article}>
                      <GatedRoute
                        path={[`/people/segments/:id(${UUIDRegex})`, '/people']}
                        gateFunction={userHasPeopleAccess}
                        render={({
                          match: {
                            params: { id },
                          },
                        }) => (
                          <SegmentsMenu
                            type="Person"
                            segmentId={id}
                            key="person"
                          />
                        )}
                      />
                      <GatedRoute
                        path={[
                          `/companies/segments/:id(${UUIDRegex})`,
                          '/companies',
                        ]}
                        gateFunction={userHasCompaniesAccess}
                        render={({
                          match: {
                            params: { id },
                          },
                        }) => (
                          <SegmentsMenu
                            type="Company"
                            segmentId={id}
                            key="company"
                          />
                        )}
                      />

                      <Suspense fallback={<LoadingArea loading />}>
                        <Switch>
                          <Route exact path="/no-access" component={NoAccess} />

                          <GatedRoute
                            exact
                            path={['/companies/import']}
                            gateFunction={userHasCompaniesAccess}
                            render={() => (
                              <ImportCsv key="Company" type="Company" />
                            )}
                          />

                          <GatedRoute
                            exact
                            path={['/people/import']}
                            gateFunction={userHasPeopleAccess}
                            render={() => (
                              <ImportCsv key="Person" type="Person" />
                            )}
                          />

                          <GatedRoute
                            exact
                            path={[`/companies/import/:id(${UUIDRegex})`]}
                            gateFunction={userHasCompaniesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <MapCsv key="Company" id={id} type="Company" />
                            )}
                          />
                          <GatedRoute
                            exact
                            path={[`/people/import/:id(${UUIDRegex})`]}
                            gateFunction={userHasPeopleAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <MapCsv key="Person" id={id} type="Person" />}
                          />

                          <GatedRoute
                            exact
                            path={[
                              '/audiences',
                              '/audiences/configure-roi-metrics',
                            ]}
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({ match }) => {
                              const showDrawer = match.path.includes(
                                '/configure-roi-metrics',
                              );
                              return (
                                <>
                                  <SidebarDrawer
                                    open={showDrawer}
                                    onClose={() => history.push('/audiences')}>
                                    <ConfigureROIMetrics />
                                  </SidebarDrawer>
                                  <Audiences />
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={[
                              '/audiences',
                              '/audiences/export-sales-pipeline',
                            ]}
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({ match }) => {
                              const showDrawer = match.path.includes(
                                '/export-sales-pipeline',
                              );
                              return (
                                <>
                                  <SidebarDrawer
                                    open={showDrawer}
                                    onClose={() => history.push('/audiences')}>
                                    <ExportSalesPipeline />
                                  </SidebarDrawer>
                                  <Audiences />
                                </>
                              );
                            }}
                          />

                          <Route
                            path={[
                              '/audiences/access/request',
                              '/audiences/access',
                            ]}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  unmountOnExit
                                  timeout={500}>
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        open={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onClose={() => {
                                          history.push(paths.audiencesAccess());
                                        }}>
                                        <ContactSuccess
                                          product="advertising"
                                          backPath={paths.audiencesAccess()}
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <AudiencesAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            path="/audiences/new/:audienceSubtype(company-segment-prospects|contacts|managed-visitors|prospects|visitors)?/:step(sync)?"
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({
                              match: {
                                params: { audienceSubtype, step },
                              },
                              location,
                            }) => {
                              const {
                                segmentId,
                                segmentType,
                              } = queryString.parse(location.search);

                              const stepIndex = [audienceSubtype, step].filter(
                                Boolean,
                              ).length;

                              return (
                                <AudienceNew
                                  key={audienceSubtype}
                                  segmentId={segmentId}
                                  segmentType={segmentType}
                                  stepIndex={stepIndex}
                                  subtype={audienceSubtype?.replace(/-/g, '_')}
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={`/audiences/:id(${UUIDRegex})`}
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <Audience id={id} />}
                          />

                          <GatedRoute
                            exact
                            path="/audiences/:id/edit"
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <AudienceEdit id={id} />}
                          />

                          <GatedRoute
                            exact
                            path="/audiences/:id/clone/:step(sync)?"
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <AudienceClone id={id} />}
                          />

                          <GatedRoute
                            exact
                            path={`/companies/:companyId(${UUIDRegex})?`}
                            component={Companies}
                            gateFunction={userHasCompaniesAccess}
                          />

                          <GatedRoute
                            exact
                            path="/companies/segments/new"
                            gateFunction={userHasCompaniesAccess}
                            render={({
                              history: {
                                location: { state = {} },
                              },
                            }) => {
                              const { collection_id: collectionId } = state;

                              return (
                                <SegmentNew
                                  collectionId={collectionId}
                                  key={collectionId}
                                  type="Company"
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={`/companies/segments/:id(${UUIDRegex})/edit`}
                            gateFunction={userHasCompaniesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <SegmentEdit
                                id={id}
                                type={segmentTypes.company}
                                attributeType="Company"
                                collectionType="Company"
                                legacyType="Company"
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            path={[
                              `/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                              `/companies/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product?`,
                            ]}
                            gateFunction={userHasCompaniesAccess}
                            render={({
                              match: {
                                params: { action, id, product, segmentId },
                              },
                              location,
                            }) => {
                              const searchParams = new URLSearchParams(
                                location.search,
                              );

                              return (
                                <>
                                  <SegmentDestinations
                                    segmentId={segmentId}
                                    segmentType={segmentTypes.company}
                                  />
                                  <SidebarDrawer
                                    open={!!action}
                                    onClose={() => {
                                      history.push(
                                        paths.segmentDestinations(
                                          segmentTypes.company,
                                          segmentId,
                                        ),
                                      );
                                    }}>
                                    {(() => {
                                      switch (action) {
                                        case 'new':
                                          return (
                                            <SegmentDestinationNew
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                              type={searchParams.get('type')}
                                            />
                                          );
                                        case 'edit':
                                          return (
                                            <SegmentDestinationEdit
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                              destinationId={id}
                                            />
                                          );
                                        case 'contact-success':
                                          return (
                                            <ContactSuccess
                                              product={product}
                                              backPath={paths.segmentDestinations(
                                                segmentTypes.company,
                                                segmentId,
                                              )}
                                            />
                                          );
                                        default:
                                          // A default is needed when the segment is animating in and out
                                          return (
                                            <SegmentDestinationSkeleton
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                            />
                                          );
                                      }
                                    })()}
                                  </SidebarDrawer>
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={`/companies/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                            gateFunction={userHasCompaniesAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <CompaniesSegment id={id} key={id} />}
                          />

                          <Redirect
                            exact
                            from={`/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                            to="/companies/segments/:segmentId/destinations/:id/edit"
                          />

                          <GatedRoute
                            exact
                            path={`/people/:personId(${UUIDRegex})?`}
                            component={People}
                            gateFunction={userHasPeopleAccess}
                          />

                          <GatedRoute
                            exact
                            path="/people/segments/new"
                            gateFunction={userHasPeopleAccess}
                            render={({
                              history: {
                                location: { state = {} },
                              },
                            }) => {
                              const { collection_id: collectionId } = state;

                              return (
                                <SegmentNew
                                  collectionId={collectionId}
                                  key={collectionId}
                                  type="Person"
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={`/people/segments/:id(${UUIDRegex})/edit`}
                            gateFunction={userHasPeopleAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <SegmentEdit
                                id={id}
                                type={segmentTypes.person}
                                attributeType="Person"
                                collectionType="Person"
                                legacyType="Person"
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            path={[
                              `/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                              `/people/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product`,
                            ]}
                            gateFunction={userHasPeopleAccess}
                            render={({
                              match: {
                                params: { action, id, product, segmentId },
                              },
                              location,
                            }) => {
                              const searchParams = new URLSearchParams(
                                location.search,
                              );

                              return (
                                <>
                                  <SegmentDestinations
                                    segmentId={segmentId}
                                    segmentType={segmentTypes.person}
                                  />
                                  <SidebarDrawer
                                    open={!!action}
                                    onClose={() =>
                                      history.push(
                                        paths.segmentDestinations(
                                          segmentTypes.person,
                                          segmentId,
                                        ),
                                      )
                                    }>
                                    {(() => {
                                      switch (action) {
                                        case 'new':
                                          return (
                                            <SegmentDestinationNew
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                              type={searchParams.get('type')}
                                            />
                                          );
                                        case 'edit':
                                          return (
                                            <SegmentDestinationEdit
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                              destinationId={id}
                                            />
                                          );
                                        case 'contact-success':
                                          return (
                                            <ContactSuccess
                                              product={product}
                                              backPath={paths.segmentDestinations(
                                                segmentTypes.person,
                                                segmentId,
                                              )}
                                            />
                                          );
                                        default:
                                          // A default is needed when the segment is animating in and out
                                          return (
                                            <SegmentDestinationSkeleton
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                            />
                                          );
                                      }
                                    })()}
                                  </SidebarDrawer>
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            path={`/people/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                            gateFunction={userHasPeopleAccess}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <PeopleSegment id={id} key={id} />}
                          />

                          <Redirect
                            exact
                            from={`/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                            to="/people/segments/:segmentId/destinations/:id/edit"
                          />

                          <GatedRoute
                            path="/settings/configuration/tagsjs"
                            gateFunction={userHasSettingsAccess}>
                            <TagsJs />
                          </GatedRoute>

                          <GatedRoute
                            path="/settings/configuration/segment"
                            gateFunction={userHasSettingsAccess}>
                            <Segment />
                          </GatedRoute>

                          <GatedRoute
                            path="/settings/configuration/:service/connect"
                            gateFunction={userHasSettingsAccess}
                            render={({
                              match: {
                                params: { service },
                              },
                            }) => <Disconnected service={service} />}
                          />

                          <GatedRoute
                            path="/settings/configuration/:service"
                            gateFunction={userHasSettingsAccess}
                            render={({
                              match: {
                                params: { service },
                              },
                            }) => (
                              <ConfigurationPanelFactory config={service} />
                            )}
                          />

                          <GatedRoute
                            path="/settings/configuration"
                            component={Configurations}
                            gateFunction={userHasSettingsAccess}
                          />

                          <GatedRoute
                            exact
                            path={paths.idealCustomerProfileNew()}
                            gateFunction={userHasICP}
                            component={IdealCustomerProfileNew}
                          />

                          <GatedRoute
                            path={paths.idealCustomerProfile()}
                            gateFunction={userHasICP}
                            component={IdealCustomerProfile}
                          />

                          <GatedRoute
                            exact
                            path="/welcome"
                            component={Welcome}
                            gateFunction={userHasSetupAccess}
                          />

                          <Route path="/onboarding" component={Onboarding} />

                          <GatedRoute
                            exact
                            path={['/', '/news/:id']}
                            component={Dashboard}
                            gateFunction={userHasDashboardAccess}
                          />

                          <GatedRoute
                            path="/sandbox/:component?"
                            component={Sandbox}
                            gateFunction={userHasInternalAccess}
                          />

                          <GatedRoute
                            exact
                            path="/enrichment"
                            gateFunction={userHasDataEnrichmentAccess}
                            fallbackPath={paths.dataEnrichmentAccess()}
                            render={() => <EnrichmentList />}
                          />

                          <Route
                            path={[
                              '/enrichment/access/request',
                              '/enrichment/access',
                            ]}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  unmountOnExit
                                  timeout={500}>
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        open={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onClose={() => {
                                          history.push(
                                            paths.dataEnrichmentAccess(),
                                          );
                                        }}>
                                        <ContactSuccess
                                          product="data_enrichment"
                                          backPath={paths.dataEnrichmentAccess()}
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <EnrichmentAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            path={[
                              `/enrichment/:integration(${concatEnrichmentTypes})/logs`,
                              `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                            ]}
                            gateFunction={userHasDataEnrichmentLogsAccess}
                            render={({
                              match: {
                                params: { integration },
                              },
                            }) => (
                              <EnrichmentLogs
                                enrichmentLogsType={integration}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            path={[
                              `/enrichment/:integration(${concatEnrichmentTypes})`,
                              `/enrichment/:integration(${concatEnrichmentTypes})/:objectType`,
                            ]}
                            gateFunction={userHasDataEnrichmentAccess}
                            render={({
                              match: {
                                params: { integration, objectType },
                              },
                            }) => (
                              <EnrichmentShow
                                integration={integration}
                                objectType={objectType}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            path="/forms"
                            gateFunction={userHasFormsAccess}
                            fallbackPath={paths.formsAccess()}
                            render={() => <FormsList />}
                          />

                          <Route
                            path={['/forms/access/request', '/forms/access']}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  unmountOnExit
                                  timeout={500}>
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        open={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onClose={() => {
                                          history.push(paths.formsAccess());
                                        }}>
                                        <ContactSuccess
                                          product="form_shortening"
                                          backPath={paths.formsAccess()}
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <FormsAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            path="/forms/:integration"
                            gateFunction={userHasFormsAccess}
                            render={({
                              match: {
                                params: { integration },
                              },
                            }) => <FormsShow integration={integration} />}
                          />

                          <GatedRoute
                            exact
                            path="/forms/:integration/:selectedTab"
                            gateFunction={userHasFormsAccess}
                            render={({
                              match: {
                                params: { integration, selectedTab },
                              },
                            }) => (
                              <FormsShow
                                integration={integration}
                                selectedTab={selectedTab}
                              />
                            )}
                          />

                          <Route>
                            <MissingPage />
                          </Route>
                        </Switch>
                      </Suspense>

                      {/* Sidebars */}
                      <Switch>
                        <Route
                          path={[
                            `/companies/segments/:segmentId/:id(${UUIDRegex})`,
                            `/people/segments/:segmentId/:id(${UUIDRegex})`,
                            `/companies/:id(${UUIDRegex})`,
                            `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                            `/people/:id(${UUIDRegex})`,
                            `/people-new/:id(${UUIDRegex})`,
                            '/companies',
                            `/enrichment/:integration(${concatEnrichmentTypes})/logs`,
                            '/people',
                          ]}
                          render={({ location, match }) => (
                            <TransitionGroup>
                              <Transition
                                key={match.path}
                                unmountOnExit
                                timeout={500}>
                                {(state) => (
                                  <ErrorHandler>
                                    <Switch location={location}>
                                      <GatedRoute
                                        exact
                                        gateFunction={userHasCompaniesAccess}
                                        path={[
                                          `/companies/:id(${UUIDRegex})`,
                                          `/companies/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { id, segmentId },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            open={state === 'entered'}
                                            onClose={() => {
                                              history.push(
                                                paths.segment(
                                                  segmentTypes.company,
                                                  segmentId,
                                                ),
                                              );
                                            }}>
                                            <Company id={id} />
                                          </SidebarDrawer>
                                        )}
                                      />

                                      <GatedRoute
                                        exact
                                        gateFunction={
                                          userHasDataEnrichmentLogsAccess
                                        }
                                        path={[
                                          `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { integration, id },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            open={state === 'entered'}
                                            onClose={() => {
                                              history.push(
                                                paths.dataEnrichmentLogs(
                                                  integration,
                                                ),
                                              );
                                            }}>
                                            <EnrichmentLog
                                              type={integration}
                                              id={id}
                                            />
                                          </SidebarDrawer>
                                        )}
                                      />

                                      <GatedRoute
                                        exact
                                        gateFunction={userHasPeopleAccess}
                                        path={[
                                          `/people/:id(${UUIDRegex})`,
                                          `/people/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { id, segmentId },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            open={state === 'entered'}
                                            onClose={() => {
                                              history.push(
                                                paths.segment(
                                                  segmentTypes.person,
                                                  segmentId,
                                                ),
                                              );
                                            }}>
                                            <Person id={id} />
                                          </SidebarDrawer>
                                        )}
                                      />
                                    </Switch>
                                  </ErrorHandler>
                                )}
                              </Transition>
                            </TransitionGroup>
                          )}
                        />

                        <GatedRoute
                          exact
                          path={['/news/:id', '/']}
                          render={({
                            match: {
                              path,
                              params: { id },
                            },
                          }) => (
                            <TransitionGroup>
                              <Transition
                                key={path}
                                unmountOnExit
                                timeout={500}>
                                {(state) => (
                                  <SidebarDrawer
                                    onClose={() => {
                                      history.push('/');
                                    }}
                                    open={!!id && state === 'entered'}>
                                    <NewsDetail id={id} />
                                  </SidebarDrawer>
                                )}
                              </Transition>
                            </TransitionGroup>
                          )}
                          gateFunction={userHasDashboardAccess}
                        />
                      </Switch>
                    </article>
                  </ErrorHandler>
                </div>
              </MuiThemeProvider>
              <Route
                path="/"
                render={() => {
                  if (
                    window.analytics &&
                    typeof window.analytics.page === 'function'
                  ) {
                    window.analytics.page(history.location.pathname);
                  }
                  return null;
                }}
              />
            </Router>
          </Provider>
        </ApolloProvider>
      </QueryClientProvider>
    );
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
};

const StyledApp = withStyles(styles)(App);
export default StyledApp;
